import { WarningIcon } from '@chakra-ui/icons';
import {
    AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
    Button, Center,
    Checkbox,
    FormControl, FormErrorMessage,
    HStack, Input, Text,
    VStack
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { sendPatientValidationLink } from '../../../api';

export default function PatientsValidationSend({ patient }) {
    const cancelRef = useRef();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [targetPatient, setTargetPatient] = useState(null);
    const [selectedMethod, setSelectedMethod] = useState('email');
    const [tempEmail, setTempEmail] = useState('');
    const [tempPhone, setTempPhone] = useState('');
    const [sending, setSending] = useState(false);
    const [issues, setIssues] = useState([]);
    const [selectedIssues, setSelectedIssues] = useState([]);

    // inline errors
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [issuesError, setIssuesError] = useState('');

    const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    const isValidPhone = (v) => String(v || '').replace(/\D/g, '').length >= 10;

    const openValidation = (p) => {
        setTargetPatient(p);
        const email = p?.email || p?.contact?.email || '';
        const phone = p?.phone || p?.contact?.phone || '';
        setTempEmail(email);
        setTempPhone(phone);
        setSelectedMethod(email ? 'email' : 'sms');

        const hasMissingIdPhotos = !(p.governmentId?.frontImageUrl && p.governmentId?.backImageUrl);
        const hasMissingInsPhotos = !(p.insurancePrimary?.cardFrontUrl && p.insurancePrimary?.cardBackUrl);
        const today = new Date(); today.setHours(0, 0, 0, 0);

        const iss = [];
        if (hasMissingIdPhotos) iss.push({ label: "Photo ID/DL missing", short: "ID" });
        if (hasMissingInsPhotos) iss.push({ label: "Insurance card missing", short: "IC" });

        setIssues(iss);
        setSelectedIssues(iss.map(i => i.short)); // default: all checked

        setEmailError(''); setPhoneError(''); setIssuesError('');
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setTargetPatient(null);
    };

    const toggleIssue = (issue) => {
        setSelectedIssues((prev) =>
            prev.includes(issue) ? prev.filter((i) => i !== issue) : [...prev, issue]
        );
    };

    // replace your handleSend with this:
    const handleSend = async () => {
        setEmailError(''); setPhoneError(''); setIssuesError('');

        if (selectedIssues.length === 0) {
            setIssuesError('Select at least one item to request.');
            return;
        }
        if (selectedMethod === 'email') {
            if (!tempEmail || !isValidEmail(tempEmail)) {
                setEmailError('Enter a valid email.');
                return;
            }
        } else {
            if (!tempPhone || !isValidPhone(tempPhone)) {
                setPhoneError('Enter a valid phone (10+ digits).');
                return;
            }
        }

        setSending(true);
        try {
            const value = selectedMethod === 'email' ? tempEmail : tempPhone;
            const res = await sendPatientValidationLink(
                targetPatient?._id,
                selectedMethod,
                value,
                selectedIssues
            );
            console.log('SEND_RES', res);
        } catch (e) {
            console.error('SEND_ERR', e);
        } finally {
            setSending(false);
            closeDialog();
        }
    };



    return (
        <>


            <Center mt={5} mb={5}>
                <Button size="sm" colorScheme="purple" onClick={() => openValidation(patient)}>
                    <WarningIcon mr={1} /> Request Validation
                </Button>
            </Center>

            <AlertDialog isOpen={dialogOpen} leastDestructiveRef={cancelRef} onClose={closeDialog}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Send Validation Link
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            <Text mb={2}>
                                Request from <strong>{targetPatient?.firstName} {targetPatient?.lastName}</strong>:
                            </Text>

                            {/* Issues as checkboxes */}
                            <FormControl isInvalid={!!issuesError} mb={4}>
                                <VStack align="start" spacing={2}>
                                    {issues?.length === 0 && <Text fontSize="sm">No issues detected.</Text>}
                                    {issues?.map((issue, i) => (
                                        <Checkbox
                                            key={i}
                                            isChecked={selectedIssues.includes(issue.short)}
                                            onChange={() => toggleIssue(issue.short)}
                                        >
                                            {issue.label}
                                        </Checkbox>
                                    ))}

                                </VStack>
                                <FormErrorMessage>{issuesError}</FormErrorMessage>
                            </FormControl>

                            {/* Method toggle */}
                            <Center>
                                <HStack mb={4} mt={2}>
                                    <Button
                                        size="sm"
                                        variant={selectedMethod === 'email' ? 'solid' : 'outline'}
                                        colorScheme="blue"
                                        onClick={() => setSelectedMethod('email')}
                                    >
                                        Email
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant={selectedMethod === 'sms' ? 'solid' : 'outline'}
                                        colorScheme="green"
                                        onClick={() => setSelectedMethod('sms')}
                                    >
                                        Phone
                                    </Button>
                                </HStack>
                            </Center>

                            {/* Inputs */}
                            {selectedMethod === 'email' && (
                                <FormControl isInvalid={!!emailError}>
                                    <Text fontSize="sm" mb={1}>Email</Text>
                                    <Input value={tempEmail} onChange={(e) => setTempEmail(e.target.value)} />
                                    <FormErrorMessage>{emailError}</FormErrorMessage>
                                </FormControl>
                            )}

                            {selectedMethod === 'sms' && (
                                <FormControl isInvalid={!!phoneError}>
                                    <Text fontSize="sm" mb={1}>Phone</Text>
                                    <Input value={tempPhone} onChange={(e) => setTempPhone(e.target.value)} />
                                    <FormErrorMessage>{phoneError}</FormErrorMessage>
                                </FormControl>
                            )}
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={closeDialog}>Cancel</Button>
                            <Button
                                colorScheme="orange"
                                ml={3}
                                isLoading={sending}
                                isDisabled={selectedIssues.length === 0}
                                onClick={handleSend}
                            >
                                Send Link
                            </Button>

                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
}
